<template>
  <div class="relative">
    <!-- Header mobile/tablet (visible bajo lg) -->
    <header
      class="lg:hidden fixed top-0 inset-x-0 z-30 h-14 bg-white border-b border-base-gray-extraLight flex items-center justify-between px-4 shadow-sm"
    >
      <button
        type="button"
        @click="openMobileSidebar"
        class="flex items-center justify-center w-10 h-10 rounded-[10px] hover:bg-base-gray-ultraLight active:bg-base-gray-soft outline-none focus:outline-none transition-colors duration-150"
        aria-label="Abrir menú"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="3" y1="6" x2="21" y2="6"/>
          <line x1="3" y1="12" x2="21" y2="12"/>
          <line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </button>
      <router-link to="/" class="flex items-center outline-none focus-visible:outline-none">
        <base-icon name="julmar" class="text-base-gray-dark max-w-[99px] h-auto" />
      </router-link>
      <!-- Spacer para balancear el hamburger -->
      <div class="w-10 h-10"></div>
    </header>

    <!-- Overlay backdrop mobile -->
    <transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="mobileOpen"
        class="lg:hidden fixed inset-0 bg-black/40 z-40"
        @click="closeMobileSidebar"
      ></div>
    </transition>

    <!-- Sidebar: drawer en mobile/tablet, fijo en desktop -->
    <div
      class="fixed inset-y-0 left-0 flex flex-col transform transition-transform duration-300 lg:transition-all bg-white border-r border-gray-200 h-screen w-72"
      :class="[
        // Ancho desktop según colapsado
        isCollapsed ? 'lg:w-24' : 'lg:w-72',
        // Visibilidad mobile vs desktop
        mobileOpen
          ? 'translate-x-0 z-50 pointer-events-auto'
          : '-translate-x-full lg:translate-x-0 pointer-events-none lg:pointer-events-auto',
        sidebarZIndex
      ]"
    >
      <!-- Botón collapse desktop -->
      <button
        @click="toggleSidebar"
        class="hidden lg:flex absolute top-10 justify-center items-center bg-white border-2 border-base-gray-lighter p-2 rounded-full transition-all w-[37px] h-[37px] cursor-pointer outline-none focus:outline-none"
        :class="{ 'left-[74px]': isCollapsed, 'left-[268px]': !isCollapsed }"
      >
        <base-icon
          name="sidebar-arrow"
          class="text-base-gray-light cursor-pointer"
          :class="{
            'transform rotate-180': !isCollapsed,
            'transform rotate-0': isCollapsed
          }"
        />
      </button>
      <!-- Botón cerrar drawer (solo mobile) -->
      <button
        type="button"
        @click="closeMobileSidebar"
        class="lg:hidden absolute top-3 right-3 flex items-center justify-center w-9 h-9 rounded-full hover:bg-base-gray-ultraLight active:bg-base-gray-soft outline-none focus:outline-none transition-colors duration-150"
        aria-label="Cerrar menú"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
      <div class="flex grow flex-col gap-y-5 border-r border-gray-200 bg-white h-full overflow-y-auto">
        <div class="flex h-16 shrink-0 items-center px-6">
          <transition name="slide-fade">
            <router-link
              to="/"
              class="flex items-center text-base-gray-dark text-sm font-normal leading-normal outline-none focus-visible:outline-none h-auto w-full px-2 py-9"
              v-show="!isCollapsed"
            >
              <base-icon
                name="julmar"
                class="text-base-gray-dark max-w-[94px] h-auto"
              />
            </router-link>
          </transition>
        </div>
        <nav class="flex flex-1 flex-col justify-between px-6">
          <div>
            <ul role="list" class="flex flex-1 flex-col">
              <li>
                <router-link
                  to="/admin/usuarios"
                  class="flex justify-between items-center w-full max-w-[255px] h-[52px] rounded-[10px] pl-2.5 pr-5 text-[clamp(0.875rem,-0.9855rem+2.3256vw,1rem)] outline-none focus:outline-none"
                  activeClass="bg-base-yellow bg-opacity-20"
                  :class="[
                    $route.path === '/admin/usuarios'
                      ? 'text-black font-bold'
                      : 'text-base-gray-darker hover:text-black'
                  ]"
                >
                  <div
                    class="flex gap-x-2 w-[clamp(18.75rem,6.977vw+13.169rem,19.125rem)]"
                  >
                    <base-icon name="user" class="w-[22px] h-[22px]" />
                    <transition name="slide-fade">
                      <span v-show="!isCollapsed">Usuarios</span>
                    </transition>
                  </div>
                  <base-icon
                    v-if="$route.path === '/admin/usuarios'"
                    name="chevron-menu"
                    class="w-3 h-3"
                  />
                </router-link>
              </li>
              <li>
                <router-link
                  to="/admin/catalogo"
                  class="flex justify-between items-center w-full max-w-[255px] h-[52px] rounded-[10px] pl-2.5 pr-5 text-[clamp(0.875rem,-0.9855rem+2.3256vw,1rem)] outline-none focus:outline-none"
                  activeClass="bg-base-yellow bg-opacity-20"
                  :class="[
                    $route.path === '/admin/catalogo'
                      ? 'text-black font-bold'
                      : 'text-base-gray-darker hover:text-black'
                  ]"
                >
                  <div
                    class="flex gap-x-2 w-[clamp(18.75rem,6.977vw+13.169rem,19.125rem)]"
                  >
                    <base-icon name="catalog" class="w-[22px] h-[22px]" />
                    <transition name="slide-fade">
                      <span v-show="!isCollapsed">Catálago</span>
                    </transition>
                  </div>
                  <base-icon
                    v-if="$route.path === '/admin/catalogo'"
                    name="chevron-menu"
                    class="w-3 h-3"
                  />
                </router-link>
              </li>
              <li>
                <router-link
                  to="/admin/estadisticas"
                  class="flex justify-between items-center w-full max-w-[255px] h-[52px] rounded-[10px] pl-2.5 pr-5 text-[clamp(0.875rem,-0.9855rem+2.3256vw,1rem)] outline-none focus:outline-none"
                  activeClass="bg-base-yellow bg-opacity-20"
                  :class="[
                    $route.path === '/admin/estadisticas'
                      ? 'text-black font-bold'
                      : 'text-base-gray-darker hover:text-black'
                  ]"
                >
                  <div
                    class="flex gap-x-2 w-[clamp(18.75rem,6.977vw+13.169rem,19.125rem)]"
                  >
                    <base-icon name="stats" class="w-[22px] h-[22px]" />
                    <transition name="slide-fade">
                      <span v-show="!isCollapsed">Estadísticas</span>
                    </transition>
                  </div>
                  <base-icon
                    v-if="$route.path === '/admin/estadisticas'"
                    name="chevron-menu"
                    class="w-3 h-3"
                  />
                </router-link>
              </li>
              <li>
                <router-link
                  to="/admin/banners"
                  class="flex justify-between items-center w-full max-w-[255px] h-[52px] rounded-[10px] pl-2.5 pr-5 text-[clamp(0.875rem,-0.9855rem+2.3256vw,1rem)] outline-none focus:outline-none"
                  activeClass="bg-base-yellow bg-opacity-20"
                  :class="[
                    $route.path === '/admin/banners'
                      ? 'text-black font-bold'
                      : 'text-base-gray-darker hover:text-black'
                  ]"
                >
                  <div
                    class="flex gap-x-2 w-[clamp(18.75rem,6.977vw+13.169rem,19.125rem)]"
                  >
                    <base-icon name="banners" class="w-[22px] h-[22px]" />
                    <transition name="slide-fade">
                      <span v-show="!isCollapsed">Banners</span>
                    </transition>
                  </div>
                  <base-icon
                    v-if="$route.path === '/admin/banners'"
                    name="chevron-menu"
                    class="w-3 h-3"
                  />
                </router-link>
              </li>
              <li>
                <router-link
                  to="/admin/pedidos"
                  class="flex justify-between items-center w-full max-w-[255px] h-[52px] rounded-[10px] pl-2.5 pr-5 text-[clamp(0.875rem,-0.9855rem+2.3256vw,1rem)] outline-none focus:outline-none"
                  activeClass="bg-base-yellow bg-opacity-20"
                  :class="[
                    $route.path === '/admin/pedidos'
                      ? 'text-black font-bold'
                      : 'text-base-gray-darker hover:text-black'
                  ]"
                >
                  <div
                    class="flex gap-x-2 w-[clamp(18.75rem,6.977vw+13.169rem,19.125rem)]"
                  >
                    <base-icon name="orders" class="w-[22px] h-[22px]" />
                    <transition name="slide-fade">
                      <span v-show="!isCollapsed">Pedidos</span>
                    </transition>
                  </div>
                  <base-icon
                    v-if="$route.path === '/admin/pedidos'"
                    name="chevron-menu"
                    class="w-3 h-3"
                  />
                </router-link>
              </li>
              <li>
                <router-link
                  to="/admin/reclamos"
                  class="flex justify-between items-center w-full max-w-[255px] h-[52px] rounded-[10px] pl-2.5 pr-5 text-[clamp(0.875rem,-0.9855rem+2.3256vw,1rem)] outline-none focus:outline-none"
                  activeClass="bg-base-yellow bg-opacity-20"
                  :class="[
                    $route.path === '/admin/reclamos'
                      ? 'text-black font-bold'
                      : 'text-base-gray-darker hover:text-black'
                  ]"
                >
                  <div
                    class="flex gap-x-2 w-[clamp(18.75rem,6.977vw+13.169rem,19.125rem)]"
                  >
                    <base-icon name="claims" class="w-[22px] h-[22px]" />
                    <transition name="slide-fade">
                      <span v-show="!isCollapsed">Reclamos</span>
                    </transition>
                  </div>
                  <base-icon
                    v-if="$route.path === '/admin/reclamos'"
                    name="chevron-menu"
                    class="w-3 h-3"
                  />
                </router-link>
              </li>
              <li>
                <router-link
                  to="/admin/subir"
                  class="flex justify-between items-center w-full max-w-[255px] h-[52px] rounded-[10px] pl-2.5 pr-5 text-base-gray-darker text-[clamp(0.875rem,-0.9855rem+2.3256vw,1rem)] outline-none focus:outline-none"
                  activeClass="bg-base-yellow bg-opacity-20"
                  :class="[
                    $route.path === '/admin/subir'
                      ? 'text-black font-bold'
                      : 'text-base-gray-darker hover:text-black'
                  ]"
                >
                  <div
                    class="flex gap-x-2 w-[clamp(18.75rem,6.977vw+13.169rem,19.125rem)]"
                  >
                    <base-icon name="upload" class="w-[22px] h-[22px]" />
                    <transition name="slide-fade">
                      <span class="min-w-max block" v-show="!isCollapsed">
                        Subir
                      </span>
                    </transition>
                  </div>
                  <base-icon
                    v-if="$route.path === '/admin/subir'"
                    name="chevron-menu"
                    class="w-3 h-3"
                  />
                </router-link>
              </li>
              <li>
                <router-link
                  to="/admin/web-jp"
                  class="flex justify-between items-center w-full max-w-[255px] h-[52px] rounded-[10px] pl-2.5 pr-5 text-[clamp(0.875rem,-0.9855rem+2.3256vw,1rem)] outline-none focus:outline-none"
                  activeClass="bg-base-yellow bg-opacity-20"
                  :class="[
                    $route.path === '/admin/web-jp'
                      ? 'text-black font-bold'
                      : 'text-base-gray-darker hover:text-black'
                  ]"
                >
                  <div
                    class="flex gap-x-2 w-[clamp(18.75rem,6.977vw+13.169rem,19.125rem)]"
                  >
                    <base-icon name="web" class="w-[22px] h-[22px]" />
                    <transition name="slide-fade">
                      <span class="min-w-max block" v-show="!isCollapsed">
                        Web JP Repuestos
                      </span>
                    </transition>
                  </div>
                  <base-icon
                    v-if="$route.path === '/admin/web-jp'"
                    name="chevron-menu"
                    class="w-3 h-3"
                  />
                </router-link>
              </li>
              <!-- Tienda Nube oculta por ahora (oct-2026): la ruta /admin/tienda-nube y su vista siguen andando. -->
              <li>
                <router-link
                  to="/admin/mercadolibre"
                  class="flex justify-between items-center w-full max-w-[255px] h-[52px] rounded-[10px] pl-2.5 pr-5 text-[clamp(0.875rem,-0.9855rem+2.3256vw,1rem)] outline-none focus:outline-none"
                  activeClass="bg-base-yellow bg-opacity-20"
                  :class="[
                    $route.path === '/admin/mercadolibre'
                      ? 'text-black font-bold'
                      : 'text-base-gray-darker hover:text-black'
                  ]"
                >
                  <div
                    class="flex gap-x-2 w-[clamp(18.75rem,6.977vw+13.169rem,19.125rem)]"
                  >
                    <base-icon name="catalog" class="w-[22px] h-[22px]" />
                    <transition name="slide-fade">
                      <span class="min-w-max block" v-show="!isCollapsed">
                        MercadoLibre
                      </span>
                    </transition>
                  </div>
                  <base-icon
                    v-if="$route.path === '/admin/mercadolibre'"
                    name="chevron-menu"
                    class="w-3 h-3"
                  />
                </router-link>
              </li>
              <li>
                <router-link
                  to="/admin/data-entry"
                  class="flex justify-between items-center w-full max-w-[255px] h-[52px] rounded-[10px] pl-2.5 pr-5 text-[clamp(0.875rem,-0.9855rem+2.3256vw,1rem)] outline-none focus:outline-none"
                  activeClass="bg-base-yellow bg-opacity-20"
                  :class="[
                    $route.path === '/admin/data-entry'
                      ? 'text-black font-bold'
                      : 'text-base-gray-darker hover:text-black'
                  ]"
                >
                  <div
                    class="flex gap-x-2 w-[clamp(18.75rem,6.977vw+13.169rem,19.125rem)]"
                  >
                    <base-icon name="list" class="w-[22px] h-[22px]" />
                    <transition name="slide-fade">
                      <span class="min-w-max block" v-show="!isCollapsed">
                        Data Entry
                      </span>
                    </transition>
                  </div>
                  <base-icon
                    v-if="$route.path === '/admin/data-entry'"
                    name="chevron-menu"
                    class="w-3 h-3"
                  />
                </router-link>
              </li>
              <li>
                <router-link
                  to="/admin/chat"
                  class="flex justify-between items-center w-full max-w-[255px] h-[52px] rounded-[10px] pl-2.5 pr-5 text-base-gray-darker text-[clamp(0.875rem,-0.9855rem+2.3256vw,1rem)] outline-none focus:outline-none"
                  activeClass="bg-base-yellow bg-opacity-20"
                  :class="[
                    $route.path === '/admin/chat'
                      ? 'text-black font-bold'
                      : 'text-base-gray-darker hover:text-black'
                  ]"
                >
                  <div
                    class="flex gap-x-2 w-[clamp(18.75rem,6.977vw+13.169rem,19.125rem)]"
                  >
                    <base-icon name="chat" class="w-[22px] h-[22px]" />
                    <transition name="slide-fade">
                      <span class="min-w-max block" v-show="!isCollapsed">
                        Chat
                      </span>
                    </transition>
                  </div>
                  <base-icon
                    v-if="$route.path === '/admin/chat'"
                    name="chevron-menu"
                    class="w-3 h-3"
                  />
                </router-link>
              </li>
            </ul>
          </div>

          <div>
            <ul role="list" class="flex flex-1 flex-col">
              <li>
                <router-link
                  to="/admin/configuracion"
                  class="flex justify-between items-center w-full max-w-[255px] h-[52px] rounded-[10px] pl-2.5 pr-5 text-base-gray-darker text-[clamp(0.875rem,-0.9855rem+2.3256vw,1rem)] outline-none focus:outline-none"
                  activeClass="bg-base-yellow bg-opacity-20"
                  :class="[
                    $route.path === '/admin/configuracion'
                      ? 'text-black font-bold'
                      : 'text-base-gray-darker hover:text-black'
                  ]"
                >
                  <div
                    class="flex gap-x-2 w-[clamp(18.75rem,6.977vw+13.169rem,19.125rem)]"
                  >
                    <base-icon name="settings" class="w-[22px] h-[22px]" />
                    <transition name="slide-fade">
                      <span v-show="!isCollapsed">Configuracion</span>
                    </transition>
                  </div>
                  <base-icon
                    v-if="$route.path === '/admin/configuracion'"
                    name="chevron-menu"
                    class="w-3 h-3"
                  />
                </router-link>
              </li>
              <li>
                <button
                  type="button"
                  @click="logout"
                  class="flex justify-between items-center w-full max-w-[255px] h-[52px] rounded-[10px] pl-2.5 pr-5 text-base-gray-darker text-[clamp(0.875rem,-0.9855rem+2.3256vw,1rem)] outline-none focus:outline-none"
                  activeClass="bg-base-yellow bg-opacity-20 text-black font-bold"
                >
                  <div
                    class="flex gap-x-2 w-[clamp(18.75rem,6.977vw+13.169rem,19.125rem)]"
                  >
                    <base-icon name="turn-off" class="w-[22px] h-[22px]" />
                    <transition name="slide-fade">
                      <span v-show="!isCollapsed">Salir</span>
                    </transition>
                  </div>
                </button>
              </li>
            </ul>
          </div>
        </nav>
        <div class="w-full px-6 pb-4 relative">
          <div class="h-[15px]">
            <transition name="slide-fade">
              <span class="text-[10px] min-w-max block" v-show="!isCollapsed">
                Todos los derechos reservados
                <strong>Julmar &reg; 2025</strong>
              </span>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <div
      class="min-h-screen bg-[#EBECEF] pt-14 lg:pt-0 max-w-full"
      :class="{ 'lg:pl-24': isCollapsed, 'lg:pl-72': !isCollapsed }"
    >
      <main class="p-3 2xl:p-5">
        <slot />
      </main>
    </div>
  </div>
</template>

<script>
import BaseIcon from '@/components/Base/BaseIcon.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'AdminLayout',
  components: {
    BaseIcon
  },
  data() {
    return {
      isCollapsed: false,
      showModal: false,
      modalClosed: false,
      mobileOpen: false
    }
  },
  computed: {
    ...mapGetters(['isLoggedIn']),
    sidebarZIndex() {
      // En mobile el drawer abierto necesita estar por encima del overlay.
      if (this.mobileOpen) return 'z-50'
      return this.showModal && this.modalClosed ? 'z-30' : 'z-0'
    }
  },
  methods: {
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed
    },
    openMobileSidebar() {
      // En mobile siempre se ve expandido (con labels) — forzamos !collapsed
      this.isCollapsed = false
      this.mobileOpen = true
      document.body.style.overflow = 'hidden'
    },
    closeMobileSidebar() {
      this.mobileOpen = false
      document.body.style.overflow = ''
    },
    onResize() {
      // Si pasamos a desktop (>= lg), cerramos el drawer mobile y liberamos el scroll.
      if (window.innerWidth >= 1024 && this.mobileOpen) {
        this.closeMobileSidebar()
      }
    },
    async logout() {
      this.$store.dispatch('afterSuccessLogin', null)
      this.$storage.removeStorageSync('loggedUser')
      this.$storage.removeStorageSync('showModal')
      this.$router.replace('/admin/login')
    },
    updateModalState() {
      this.showModal = !!this.$storage.getStorageSync('showModal')
    },
    listenForModalChanges() {
      window.addEventListener('storage', this.updateModalState)
      document.addEventListener('updateModalState', this.updateModalState)
    },
    removeModalListeners() {
      window.removeEventListener('storage', this.updateModalState)
      document.removeEventListener('updateModalState', this.updateModalState)
    }
  },
  async created() {
    this.emitter.on('update-modal-state', () => {
      this.updateModalState()
    })

    this.emitter.on('modal-opened', () => {
      this.modalClosed = false
    })
    this.emitter.on('modal-closed', () => {
      this.modalClosed = true
    })
  },
  mounted() {
    this.updateModalState()
    this.listenForModalChanges()
    window.addEventListener('resize', this.onResize)
  },
  beforeUnmount() {
    this.removeModalListeners()
    window.removeEventListener('resize', this.onResize)
    // Por seguridad, restaurar scroll si el componente se desmonta abierto.
    document.body.style.overflow = ''
  },
  watch: {
    showModal() {
      this.updateModalState()
    },
    // Cuando cambia la ruta cerramos el drawer mobile para que el contenido
    // sea visible inmediatamente.
    $route() {
      if (this.mobileOpen) this.closeMobileSidebar()
    }
  }
}
</script>

<style scoped>
.cart-active.router-link-active {
  background-color: rgba(75, 141, 114, 0.1);
  color: #4b8d72;
  border-bottom: 2px solid #4b8d72;
}
.slide-fade-enter-active {
  transition: transform 0.3s ease-out, opacity 0.3s;
}
.slide-fade-leave-active {
  transition: transform 0.2s ease-in, opacity 0.2s;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

/* Hovers consistentes para items de sidebar */
:deep(nav ul li > a),
:deep(nav ul li > button) {
  transition: background-color 0.15s ease-out, color 0.15s ease-out,
    transform 0.15s ease-out;
}

:deep(nav ul li > a:hover),
:deep(nav ul li > button:hover) {
  background-color: rgba(242, 168, 64, 0.08);
  color: #2a2a2a;
}

:deep(nav ul li > a.router-link-active:hover),
:deep(nav ul li > button.router-link-active:hover) {
  background-color: rgba(242, 168, 64, 0.28);
}

:deep(nav ul li > a:active),
:deep(nav ul li > button:active) {
  transform: scale(0.98);
}

/* Botón toggle del sidebar */
:deep(button[class*='top-10']):hover {
  background-color: #fffaf2;
  border-color: #f2a840;
}
:deep(button[class*='top-10']) {
  transition: background-color 0.15s ease-out, border-color 0.15s ease-out;
}
</style>
